import React, { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { Typography, TextField, Button, Paper, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, CircularProgress, Fade, makeStyles } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import axios from 'axios';
import { connect } from 'redux-zero/react';
import actions from '../../actions';

const useStyles = makeStyles(theme => ({
    topBar: {
        marginBottom: 10
    },
    topBarControl: {
        float: 'left',
        marginRight: 10
    },
    hidden: {
        display: 'none'
    },
    amount: {
        width: 80
    },
    tableContainer: {
        maxHeight: '70vh',
        overflow: 'auto'
    },
    stickyHeader: {
        position: 'sticky',
        top: 0,
        zIndex: 2,
        background: theme.palette.background.paper
    },
    frozenBodyCell: {
        position: 'sticky',
        zIndex: 1,
        background: theme.palette.background.paper
    },
    frozenHeaderCell: {
        position: 'sticky',
        top: 0,
        zIndex: 3,
        background: theme.palette.background.paper
    },
    frozenBoundary: {
        borderRight: `1px solid ${theme.palette.divider}`
    },
    highlightHeader: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText
    },
    highlightFirstCell: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText
    },
    highlightHeaderHover: {
        backgroundColor: theme.palette.grey[200]
    },
    highlightFirstCellHover: {
        backgroundColor: theme.palette.grey[200]
    }
}));

const mapToProps = ({ selectedSociety }) => ({ selectedSociety });

const FROZEN_COLUMNS = [
    { label: 'Code', width: 70, getValue: row => row.memberAccountCode },
    { label: 'Flat', width: 140, getValue: row => [row.buildingNo, row.wing, row.unit].filter(Boolean).join('-') },
    { label: 'Name', width: 150, getValue: row => row.name }
];

// Item indexes of the non-occupancy and maintenance bill items when both are set in settings and present in
// the bill master, otherwise null. Non-occupancy is then calculated and cannot be edited.
function getNonOccupancyIndexes(billMaster) {
    if (!billMaster.nonOccupancyAccountCode || !billMaster.maintenanceAccountCode) return null;

    const nonOccupancy = billMaster.billItems.find(x => x.code === billMaster.nonOccupancyAccountCode);
    const maintenance = billMaster.billItems.find(x => x.code === billMaster.maintenanceAccountCode);
    if (!nonOccupancy || !maintenance) return null;

    return { nonOccupancy: nonOccupancy.itemIndex, maintenance: maintenance.itemIndex };
}

// 10% of maintenance rounded to the rupee for LL members, 0 for others. Same as BillMasterService.RecalculateNonOccupancy
function calculateNonOccupancy(ll, maintenance) {
    return ll ? Math.round((maintenance || 0) * 0.1) : 0;
}

const FROZEN_COLUMN_OFFSETS = (() => {
    let acc = 0;
    return FROZEN_COLUMNS.map(col => { const left = acc; acc += col.width; return left; });
})();

function BillMasters({ selectedSociety }) {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = React.useState(false);
    const [refreshData, setRefreshData] = React.useState(1);
    const [refreshTable, setRefreshTable] = React.useState(1);
    const [billMasters, setBillMasters] = useState([]);
    const [selectedMaster, setSelectedMaster] = useState({});
    const [billMaster, setBillMaster] = useState({ billItems: [], items: [] });
    const [changedCodes] = useState(new Set());
    const [focusedInput, setFocusedInput] = useState(null);
    const [focusedRow, setFocusedRow] = useState(null);
    const [focusedCol, setFocusedCol] = useState(null);
    const [hoveredRow, setHoveredRow] = useState(null);
    const [hoveredCol, setHoveredCol] = useState(null);

    useEffect(() => {
        setLoading(true);

        axios.get('/billmaster/list', { headers: { 'X-SocietyId': selectedSociety } })
            .then(response => {
                setBillMasters(response.data);
            });
    },
        [selectedSociety, refreshData]);

    useEffect(() => {
        if (billMasters.length > 0) {
            let current = billMasters.find(o => o.name === 'Current');
            setSelectedMaster(current);
        }
    }, [setSelectedMaster, billMasters]);

    useEffect(() => {
        if (selectedMaster.id) {
            axios.get('/billmaster/items/' + selectedMaster.id, { headers: { 'X-SocietyId': selectedSociety } })
                .then(response => {
                    let v = response.data;
                    // Show the calculated non-occupancy. The server recalculates it when the bill master is saved
                    // and before bills are generated. Only the current bill master is recalculated.
                    let indexes = selectedMaster.name === 'Current' ? getNonOccupancyIndexes(v) : null;
                    for (let item of v.items) {
                        if (indexes) {
                            item['a' + indexes.nonOccupancy] = calculateNonOccupancy(item.ll, item['a' + indexes.maintenance]);
                        }
                        calculateTotal(item);
                    }
                    setBillMaster(v);
                    setRefreshTable(refreshTable + 1);
                    setLoading(false);
                })
                .catch(response => {
                    setLoading(false);
                });
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedMaster]);

    function calculateTotal(item) {
        item.total = item.a1 + item.a2 + item.a3 + item.a4 + item.a5 + item.a6 + item.a7 + item.a8 + item.a9 + item.a10 +
            item.a11 + item.a12 + item.a13 + item.a14 + item.a15 + item.a16 + item.a17 + item.a18 + item.a19 + item.a20;
    }

    const nonOccupancyIndexes = selectedMaster.name === 'Current' ? getNonOccupancyIndexes(billMaster) : null;

    function handleRowChange(e, row, a) {
        changedCodes.add(row.memberAccountCode);
        row[a] = parseFloat(e.target.value);
        if (nonOccupancyIndexes && a === 'a' + nonOccupancyIndexes.maintenance) {
            updateNonOccupancy(row);
        }
        calculateTotal(row);
        let totalCell = document.getElementById('total_' + row.memberAccountCode);
        totalCell.innerHTML = row.total;
    }

    // Recalculates non-occupancy from the maintenance input of the row
    function updateNonOccupancy(row) {
        let code = row.memberAccountCode;
        let maintenanceInput = document.getElementById('a' + nonOccupancyIndexes.maintenance + '_' + code);
        let nonOccupancyInput = document.getElementById('a' + nonOccupancyIndexes.nonOccupancy + '_' + code);
        let nonOccupancy = calculateNonOccupancy(row.ll, parseFloat(maintenanceInput.value));
        row['a' + nonOccupancyIndexes.nonOccupancy] = nonOccupancy;
        nonOccupancyInput.value = nonOccupancy;
    }

    const FIXED_COLUMN_COUNT = FROZEN_COLUMNS.length;

    function headerCellClass(colIndex, stickyClass) {
        if (focusedCol === colIndex) return `${stickyClass} ${classes.highlightHeader}`.trim();
        if (hoveredCol === colIndex) return `${stickyClass} ${classes.highlightHeaderHover}`.trim();
        return stickyClass;
    }

    function frozenColumnStyle(colIndex) {
        let width = FROZEN_COLUMNS[colIndex].width;
        return { left: FROZEN_COLUMN_OFFSETS[colIndex], width, minWidth: width, maxWidth: width };
    }

    function frozenHeaderCellClass(colIndex) {
        let isBoundary = colIndex === FROZEN_COLUMNS.length - 1;
        return headerCellClass(colIndex, `${classes.frozenHeaderCell} ${isBoundary ? classes.frozenBoundary : ''}`.trim());
    }

    function frozenBodyCellClass(colIndex, rowIndex) {
        let isBoundary = colIndex === FROZEN_COLUMNS.length - 1;
        let highlightClass = '';
        if (focusedRow === rowIndex) {
            highlightClass = classes.highlightFirstCell;
        } else if (hoveredRow === rowIndex || hoveredCol === colIndex) {
            highlightClass = classes.highlightFirstCellHover;
        }
        return `${classes.frozenBodyCell} ${isBoundary ? classes.frozenBoundary : ''} ${highlightClass}`.trim();
    }

    function bodyColumnHoverClass(colIndex) {
        return hoveredCol === colIndex ? classes.highlightFirstCellHover : '';
    }

    function hoverHandlers(rowIndex, colIndex) {
        return {
            onMouseEnter: () => { setHoveredRow(rowIndex); setHoveredCol(colIndex); }
        };
    }

    function renderBillItemHeaders() {
        var ret = [];
        billMaster.billItems.forEach((billItem, i) => {
            ret.push(
                <TableCell key={billItem.name} className={headerCellClass(FIXED_COLUMN_COUNT + i, classes.stickyHeader)}>
                    {billItem.name}
                </TableCell>
            );
        });
        ret.push(
            <TableCell className={headerCellClass(FIXED_COLUMN_COUNT + billMaster.billItems.length, classes.stickyHeader)}>
                Total
            </TableCell>
        );
        return ret;
    }

    function renderBillItemAmountCells(row, rowIndex) {
        var ret = [];
        billMaster.billItems.forEach((billItem, i) => {
            let colIndex = FIXED_COLUMN_COUNT + i;
            ret.push(
                <TableCell key={billItem.name}
                    className={bodyColumnHoverClass(colIndex)}
                    {...hoverHandlers(rowIndex, colIndex)}
                >
                    <TextField type="number" className={classes.amount}
                        disabled={nonOccupancyIndexes != null && billItem.itemIndex === nonOccupancyIndexes.nonOccupancy}
                        id={'a' + billItem.itemIndex + '_' + row.memberAccountCode}
                        inputProps={{ code: row.memberAccountCode }}
                        defaultValue={row['a' + billItem.itemIndex]}
                        onChange={(e) => handleRowChange(e, row, 'a' + billItem.itemIndex)}
                        onFocus={(e) => { setFocusedInput(e.target); setFocusedRow(rowIndex); setFocusedCol(colIndex); }}
                        onBlur={() => { setFocusedRow(null); setFocusedCol(null); }}
                        onWheel={(e) => e.target.blur()}
                        onKeyUp={(e) => {
                            if (e.keyCode === 13) {
                                let form = e.target.form;
                                let index = Array.prototype.indexOf.call(form, e.target);
                                let nextElement = form.elements[index + billMaster.billItems.length * 2];
                                if (nextElement) {
                                    nextElement.focus();
                                    nextElement.select();
                                }
                            }
                        }}
                    />
                </TableCell>);
        });
        let totalColIndex = FIXED_COLUMN_COUNT + billMaster.billItems.length;
        ret.push(
            <TableCell id={'total_' + row.memberAccountCode}
                className={bodyColumnHoverClass(totalColIndex)}
                {...hoverHandlers(rowIndex, totalColIndex)}
            >
                {row.total}
            </TableCell>
        );
        return ret;
    }

    function save() {
        let changedRows = [];
        for (let code of changedCodes) {
            var r = {
                billMasterId: selectedMaster.id, memberAccountCode: code,
                a1: 0, a2: 0, a3: 0, a4: 0, a5: 0, a6: 0, a7: 0, a8: 0, a9: 0, a10: 0,
                a11: 0, a12: 0, a13: 0, a14: 0, a15: 0, a16: 0, a17: 0, a18: 0, a19: 0, a20: 0
            };
            for (let i = 1; i <= 20; i++) {
                var id = 'a' + i + '_' + code;
                let t = document.getElementById(id);
                if (t) {
                    if (!t.value) t.value = 0;
                    r['a' + i] = parseFloat(t.value);
                } else {
                    delete r['a' + i];
                }
            }

            changedRows.push(r);
        }

        if (changedRows.length > 0) {
            axios({
                method: 'PUT',
                url: '/billmaster/items',
                headers: { 'X-SocietyId': selectedSociety },
                data: JSON.stringify(changedRows)
            })
                .then(response => {
                    changedCodes.clear();
                    setRefreshData(refreshData + 1);
                    enqueueSnackbar('Saved', { variant: 'success' });
                })
                .catch(error => {
                    alert(error.response.data.message);
                });
        } else {
            enqueueSnackbar('No changes have been made');
        }
    }

    function copyDown() {
        if (!focusedInput) return;

        var form = focusedInput.form;
        var index = Array.prototype.indexOf.call(form, focusedInput);

        while (true) {
            var nextIndex = index + billMaster.billItems.length * 2;

            if (nextIndex < form.elements.length) {
                var nextInput = form.elements[nextIndex];
                nextInput.value = focusedInput.value;
                const code = parseInt(nextInput.getAttribute('code'));
                changedCodes.add(code);
                if (nonOccupancyIndexes && focusedInput.id.startsWith('a' + nonOccupancyIndexes.maintenance + '_')) {
                    let row = billMaster.items.find(x => x.memberAccountCode === code);
                    updateNonOccupancy(row);
                    row['a' + nonOccupancyIndexes.maintenance] = parseFloat(nextInput.value);
                    calculateTotal(row);
                    document.getElementById('total_' + code).innerHTML = row.total;
                }
                index = nextIndex;
            } else {
                break;
            }
        }
    }

    return (
        <>
            <Typography variant="h5">Bill Masters</Typography>
            <div className={classes.topBar}>
                <Autocomplete
                    className={classes.topBarControl}
                    style={{ width: 400 }}
                    size="small"
                    options={billMasters}
                    getOptionLabel={option => option.name}
                    selectOnFocus
                    disableOpenOnFocus
                    value={selectedMaster}
                    renderInput={params => <TextField {...params} InputLabelProps={{ shrink: true }} />}
                    onChange={(_, value) => { if (value) { setSelectedMaster(value); } }}
                />
                {window.decodedJwt.role === 'Administrator' && <>
                    <Button className={classes.topBarControl} variant="contained" size="small" color="primary" onClick={save} disabled={selectedMaster.name !== 'Current'}>Save</Button>
                    <Button className={classes.topBarControl} variant="contained" size="small" color="primary" onClick={copyDown} disabled={selectedMaster.name !== 'Current'}>Copy Down</Button>
                </>
                }
                <Fade
                    in={loading}
                    style={{
                        transitionDelay: '0ms',
                    }}
                    unmountOnExit
                >
                    <CircularProgress />
                </Fade>
            </div>
            <form autoComplete="off" onKeyDown={(e) => { if (e.keyCode === 13) e.preventDefault(); }}>
                <TableContainer component={Paper} className={classes.tableContainer}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                {FROZEN_COLUMNS.map((col, colIndex) => (
                                    <TableCell key={col.label}
                                        className={frozenHeaderCellClass(colIndex)}
                                        style={frozenColumnStyle(colIndex)}
                                    >
                                        {col.label}
                                    </TableCell>
                                ))}
                                {renderBillItemHeaders()}
                            </TableRow>
                        </TableHead>
                        {<TableBody key={refreshTable}
                            onMouseLeave={() => { setHoveredRow(null); setHoveredCol(null); }}
                        >
                            {billMaster.items.map((row, rowIndex) => (
                                <TableRow
                                    key={rowIndex}
                                    className={hoveredRow === rowIndex ? classes.highlightFirstCellHover : ''}
                                >
                                    {FROZEN_COLUMNS.map((col, colIndex) => (
                                        <TableCell key={col.label}
                                            className={frozenBodyCellClass(colIndex, rowIndex)}
                                            style={frozenColumnStyle(colIndex)}
                                            {...hoverHandlers(rowIndex, colIndex)}
                                        >
                                            {col.getValue(row)}
                                        </TableCell>
                                    ))}
                                    {renderBillItemAmountCells(row, rowIndex)}
                                </TableRow>
                            ))}
                        </TableBody>}
                    </Table>
                </TableContainer>
            </form>
        </>
    );
}

export default connect(mapToProps, actions)(BillMasters);